import React, { useState, useEffect, useRef } from 'react';
import { Player } from '@lottiefiles/react-lottie-player';

/**
 * LottieRotator组件用于轮流展示多个Lottie动画
 * @param {Array} animations - Lottie动画JSON数组
 * @param {number} rotationInterval - 轮播间隔，单位毫秒
 * @param {Object} style - 组件样式
 * @param {string} className - 组件类名
 * @param {Array} animationNames - 动画名称数组
 * @param {Function} onAnimationChange - 动画切换回调
 */
const LottieRotator = ({
  animations,
  rotationInterval = 2000,
  style = {},
  className = '',
  animationNames = [],
  onAnimationChange = () => {},
  onTransitionStart = () => {},
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(null);
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // 手动强制触发重排，确保宽度过渡效果生效
  const forceReflow = (element) => {
    if (element) {
      // 读取offsetHeight属性会强制浏览器计算布局
      // eslint-disable-next-line no-unused-vars
      const reflow = element.offsetHeight;
    }
  };

  // 检测容器宽度变化
  useEffect(() => {
    // 创建ResizeObserver来监测容器宽度变化
    if (containerRef.current) {
      const observer = new ResizeObserver(entries => {
        for (let entry of entries) {
          const width = entry.contentRect.width;
          if (width !== containerWidth) {
            // 强制DOM重排以确保过渡效果
            forceReflow(containerRef.current);
            setContainerWidth(width);
            // 宽度变化时触发回调
            onAnimationChange(currentIndex, width);
          }
        }
      });

      observer.observe(containerRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [containerWidth, currentIndex, onAnimationChange]);

  useEffect(() => {
    // 只在组件内部控制动画切换
    const interval = setInterval(() => {
      setPrevIndex(currentIndex); // 保存前一个索引
      const nextIndex = (currentIndex + 1) % animations.length;

      // 先添加过渡类
      if (containerRef.current) {
        containerRef.current.classList.add('transitioning');
      }
      if (playerRef.current?.container) {
        // 上移淡出
        switch (currentIndex) {
          case 0:
            playerRef.current.container.classList.add('fadeUp');
            break;
          case 1:
            playerRef.current.container.classList.add('fadeScale');
            break;
          case 2:
            playerRef.current.container.classList.add('fade');
            break;
          case 3:
            playerRef.current.container.classList.add('fadeRightToLeft');
            break;
        }
      }

      // 延迟更新索引，确保过渡效果能够应用
      setTimeout(() => {
        setCurrentIndex(nextIndex);
        onAnimationChange(nextIndex);
      }, 1000);


      // 背景图变化需要同步
      onTransitionStart(nextIndex);

      // 过渡结束后移除过渡类
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.classList.remove('transitioning');
        }
        if (playerRef.current?.container) {
          playerRef.current.container.classList.remove('fadeUp');
          playerRef.current.container.classList.remove('fadeScale');
          playerRef.current.container.classList.remove('fade');
          playerRef.current.container.classList.remove('fadeRightToLeft');
        }
      }, 1000);
    }, rotationInterval);

    // 清理定时器
    return () => clearInterval(interval);
  }, [animations.length, rotationInterval, currentIndex, onAnimationChange]);

  return (
    <div
      ref={containerRef}
      className={`lottie-rotator ${className}`}
      style={{
        borderRadius: '8px',
        overflow: 'hidden',
        ...style,
        // transition: 'all 0.5s cubic-bezier(0.25, 0.1, 0.25, 1)'
      }}
      data-current-index={currentIndex}
      data-prev-index={prevIndex}
    >
      <Player
        ref={playerRef}
        autoplay
        // loop
        src={animations[currentIndex]}
        style={{
          width: '100%',
          height: '100%',
          transformOrigin: 'center center',
          // transition: 'all 1.5s cubic-bezier(0.25, 0.1, 0.25, 1)'
        }}
        background="transparent"
        onEvent={event => {
          if (event === 'load') playerRef.current?.play();
        }}
      />
    </div>
  );
};

export default LottieRotator;